import { ActionConfirmation, usePublishActionOutcome, useHasActionOutcomeHost, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react";
import { ApiError } from "../api.js";
import { formatDate, formatTime } from "../format.js";
import { Dialog } from "../ui/Dialog.js";
import { bookingAttemptAfterFailure, bookingFailureKind } from "./attempt.js";
import { idempotencyKey } from "./api.js";
import { SeriesCancellation } from "./SeriesCancellation.js";
import { reinstateCancelledAppointment } from "./appointmentStatus.js";
import { getRescheduleContext } from "./rescheduleContext.js";
import { staffWorkingHours } from "./staffWorkingHours.js";
import {
  staffBooking,
  type CancellationCreditResolution,
  type PaidCancellationAction,
  type RecurrenceSeriesFromAppointment,
  type TutorCancellationPreview,
  type TutorCancellationResult,
} from "./staff.js";
import { measureAction } from "../telemetry/actionTiming.js";

export function tutorCancellationFailure(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return "Something went wrong. Nothing was changed.";
}

export function tutorCancellationRetryKey(
  currentKey: string,
  cause: unknown,
  createKey: () => string,
): string {
  return bookingAttemptAfterFailure(currentKey, cause, createKey).key;
}

export function tutorCancellationSuccess(
  result: Pick<TutorCancellationResult, "credit_resolution" | "credits_restored">
    & Partial<
      Pick<
        TutorCancellationResult,
        "credit_resolution_status" | "entitlement_kind" | "credits_granted" | "credit_review_required"
      >
    >,
  notifyParticipants: boolean,
): string {
  const notificationMessage = notifyParticipants
    ? "Customer email was requested."
    : "Customer email was not requested.";

  if (result.credit_review_required || result.credit_resolution_status === "requires_attention") {
    return result.entitlement_kind === "paid_cancellation"
      ? `Cancelled. ${notificationMessage} The rebooking credit could not be added yet, so the account balance is not final. It is waiting in the Action Centre to be issued.`
      : `Cancelled. ${notificationMessage} Credit reconciliation needs staff attention before the account balance is final.`;
  }
  if (result.entitlement_kind === "paid_cancellation") {
    return (result.credits_granted ?? 0) > 0
      ? `Cancelled. ${notificationMessage} 1 rebooking credit was added to the account. The payment already made was kept; no refund was made.`
      : `Cancelled. ${notificationMessage} The payment was kept as a cancellation charge. No rebooking credit was added.`;
  }
  if (result.entitlement_kind === "requires_review") {
    return `Cancelled. ${notificationMessage} The lesson's payment has been sent to the Action Centre to check what the family is owed.`;
  }
  if (result.credit_resolution === "restore" && result.credits_restored <= 0) {
    return `Cancelled. ${notificationMessage} No lesson credit was attached to this lesson.`;
  }
  return result.credit_resolution === "restore"
    ? `Cancelled. ${notificationMessage} ${result.credits_restored} lesson ${result.credits_restored === 1 ? "credit is" : "credits are"} back on the account.`
    : `Cancelled. ${notificationMessage} The lesson credit remains used.`;
}

export function tutorCancellationNotice(hours: number): string {
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const rounded = Math.round(hours);
  return `${rounded} hour${rounded === 1 ? "" : "s"}`;
}

export function tutorCancellationIsLate(
  preview: Pick<TutorCancellationPreview, "notice_period_hours" | "notice_required_hours">,
): boolean {
  return preview.notice_period_hours < preview.notice_required_hours;
}

export function tutorCancellationRequiresChoice(
  preview: Pick<TutorCancellationPreview, "credit_resolution_required" | "credits_to_restore"> & Partial<Pick<TutorCancellationPreview, "entitlement">>,
): boolean {
  // credit_resolution_required alone doesn't prove there's nothing to
  // choose between: fall back to credits_to_restore so a lesson that
  // actually used a credit never gets an unattended "restore" default,
  // even if the backend ever reports required=false without an
  // automatic_credit_resolution for it.
  return (preview.entitlement?.decision_required === true)
    || preview.credit_resolution_required || preview.credits_to_restore > 0;
}

/** A lesson paid for directly, which never used a credit (Issue / Keep payment). */
export function tutorCancellationIsPaidLesson(
  preview: Partial<Pick<TutorCancellationPreview, "entitlement">>,
): boolean {
  return preview.entitlement?.kind === "paid_cancellation";
}

/** The paid-lesson action FastAPI will take without asking, if any. */
export function tutorCancellationAutomaticPaidAction(
  preview: Partial<Pick<TutorCancellationPreview, "entitlement">>,
): PaidCancellationAction | null {
  const action = preview.entitlement?.automatic_action;
  return tutorCancellationIsPaidLesson(preview) &&
    (action === "grant_rebooking_credit" || action === "retain_payment")
    ? action
    : null;
}

export function tutorCancellationConfirmDisabled(
  busy: boolean,
  preview: Pick<TutorCancellationPreview, "credit_resolution_required" | "credits_to_restore">
    & Partial<Pick<TutorCancellationPreview, "entitlement">>,
  resolution: CancellationCreditResolution | null,
  paidAction: PaidCancellationAction | null = null,
): boolean {
  if (busy) return true;
  if (tutorCancellationIsPaidLesson(preview)) {
    return preview.entitlement?.decision_required === true && paidAction === null;
  }
  return tutorCancellationRequiresChoice(preview) && resolution === null;
}

/**
 * The Restore button outlives the drawer that created it: closing the drawer
 * hands the cancellation receipt to the page's host, and the button is rendered
 * from there. It therefore publishes through the host it is rendered under,
 * not through the cancel form that has since unmounted.
 */
function RestoreLessonAction({
  appointmentId,
  bookingId,
  startsAt,
  cancelledAt,
  notifyParticipants,
  onRestored,
  onLocalOutcome,
}: {
  appointmentId: string;
  bookingId: string;
  startsAt: string;
  cancelledAt: number;
  notifyParticipants: boolean;
  onRestored?: () => void;
  onLocalOutcome: (outcome: ActionOutcome) => void;
}) {
  const publish = usePublishActionOutcome();
  const used = useRef(false);
  const [disabled, setDisabled] = useState(false);

  function finish(outcome: ActionOutcome) {
    if (!publish(outcome)) onLocalOutcome(outcome);
  }

  async function restore() {
    if (Date.now() - cancelledAt > 10 * 60 * 1000) {
      finish({
        variant: "attention",
        title: "Restore unavailable",
        details: [{ value: "Too late to restore here. Book the lesson again instead." }],
      });
      return;
    }

    try {
      const context = await getRescheduleContext(bookingId);
      const availability = await staffWorkingHours.check({
        providerId: context.provider_id,
        serviceId: context.service_id,
        startsAt,
      });
      if (availability.state !== "available") {
        finish({
          variant: "attention",
          title: "Restore unavailable",
          details: [{ value: "Can't restore automatically: that time may no longer be free. Book the lesson again instead." }],
        });
        return;
      }
    } catch {
      finish({
        variant: "attention",
        title: "Restore unavailable",
        details: [{ value: "Can't restore automatically: that time may no longer be free. Book the lesson again instead." }],
      });
      return;
    }

    try {
      await reinstateCancelledAppointment(
        Number(appointmentId),
        notifyParticipants,
        idempotencyKey(),
      );
      finish({
        variant: "success",
        title: "Lesson restored",
        details: onRestored ? [{ value: "The diary is refreshing." }] : [{ value: "Refresh the diary to see it" }],
      });
      onRestored?.();
    } catch (cause) {
      const uncertain = bookingFailureKind(cause) === "uncertain";
      finish({
        variant: uncertain ? "uncertain" : "attention",
        title: uncertain ? "Restore outcome needs checking" : "Could not restore lesson",
        details: [{ value: cause instanceof Error ? cause.message : "Could not restore the lesson." }],
      });
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (used.current) return;
        used.current = true;
        setDisabled(true);
        void restore();
      }}
    >
      Restore lesson
    </button>
  );
}

export function TutorCancelLesson({
  appointmentId,
  bookingId,
  studentName,
  startsAt,
  serviceName,
  onClose,
  onCancelled,
  onRestored,
  onBusyChange,
  returnFocusRef,
  presentation = "dialog",
}: {
  appointmentId: string;
  /** The Hub booking used for the exact availability check before restoration. */
  bookingId?: string;
  studentName: string;
  startsAt: string;
  serviceName: string | null;
  /**
   * The dialog was dismissed (Not now, Close, Escape, backdrop or the close
   * button after a receipt). `changed` is true when the cancellation was
   * applied or its outcome is uncertain, so the caller must re-read the diary.
   */
  onClose: (outcome: { changed: boolean }) => void;
  /** A confirmed cancellation lets the diary close this flow and reload. */
  onCancelled?: (appointmentExternalId: number) => void;
  /** Refresh the host after a successful restoration. */
  onRestored?: () => void;
  onBusyChange?: (busy: boolean) => void;
  /** Survives the action view replacing the lesson button that opened this dialog. */
  returnFocusRef?: RefObject<HTMLElement>;
  presentation?: "dialog" | "inline";
}) {
  const publish = usePublishActionOutcome();
  const hasHost = useHasActionOutcomeHost();
  function report(outcome: ActionOutcome) {
    setDone(outcome);
    if (publish(outcome)) setDialogOpen(false);
  }
  const [preview, setPreview] = useState<TutorCancellationPreview | null>(null);
  const [series, setSeries] = useState<RecurrenceSeriesFromAppointment | null>(null);
  const [seriesScope, setSeriesScope] = useState(false);
  const [seriesUncertain, setSeriesUncertain] = useState(false);
  const [resolution, setResolution] = useState<CancellationCreditResolution | null>(null);
  const [paidAction, setPaidAction] = useState<PaidCancellationAction | null>(null);
  const [notifyParticipants, setNotifyParticipants] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [done, setDone] = useState<ActionOutcome | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [key, setKey] = useState(idempotencyKey);
  const [dialogOpen, setDialogOpen] = useState(true);
  const retryPreviewRef = useRef<HTMLButtonElement>(null);
  const notNowRef = useRef<HTMLButtonElement>(null);
  const firstDecisionRef = useRef<HTMLInputElement>(null);
  const inlineHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusedReadyAction = useRef(false);
  const inlineHeadingId = useId();

  const setCancellationBusy = useCallback((nextBusy: boolean) => {
    setBusy(nextBusy);
    onBusyChange?.(nextBusy);
  }, [onBusyChange]);

  function closeDialog() {
    setDialogOpen(false);
    onClose({ changed: done !== null || uncertain || seriesUncertain });
  }

  useEffect(() => {
    if (!dialogOpen) {
      const returnTarget = returnFocusRef?.current;
      if (returnTarget?.isConnected && !returnTarget.closest("[inert]")) {
        returnTarget.focus({ preventScroll: true });
      }
      return;
    }
    if (presentation === "inline") return;
    if (busy) return;
    if (!preview) {
      if (error) retryPreviewRef.current?.focus({ preventScroll: true });
      return;
    }
    if (done || focusedReadyAction.current) return;

    const needsDecision = tutorCancellationIsPaidLesson(preview)
      ? preview.entitlement?.decision_required === true
      : tutorCancellationRequiresChoice(preview);
    (needsDecision ? firstDecisionRef.current : notNowRef.current)?.focus({ preventScroll: true });
    focusedReadyAction.current = true;
  }, [busy, dialogOpen, done, error, presentation, preview, returnFocusRef]);

  useEffect(() => {
    if (presentation === "inline" && dialogOpen) {
      inlineHeadingRef.current?.focus({ preventScroll: true });
    }
  }, [dialogOpen, presentation]);

  async function look() {
    focusedReadyAction.current = false;
    setUncertain(false);
    setCancellationBusy(true);
    setError(null);
    try {
      const next = await staffBooking.cancellationPreview(appointmentId);
      setPreview(next);
      setResolution(next.automatic_credit_resolution);
      setPaidAction(tutorCancellationAutomaticPaidAction(next));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The cancellation preview could not be loaded. No cancellation was attempted.");
    } finally {
      setCancellationBusy(false);
    }
  }

  // A lesson click already means "show me what cancelling this would do". Make
  // the authoritative preview part of opening the cancellation action rather
  // than asking for a second administrative click before the useful answer.
  useEffect(() => {
    let current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError(null);
    setUncertain(false);

    void staffBooking.cancellationPreview(appointmentId)
      .then((next) => {
        if (!current) return;
        setPreview(next);
        setResolution(next.automatic_credit_resolution);
        setPaidAction(tutorCancellationAutomaticPaidAction(next));
      })
      .catch((cause: unknown) => {
        if (current) setError(tutorCancellationFailure(cause));
      })
      .finally(() => {
        if (!current) return;
        setBusy(false);
        onBusyChange?.(false);
      });

    // Cancelling a whole series is offered in the lesson drawer only; the
    // modal used by other callers stays a single-lesson cancellation and makes
    // no recurrence request.
    if (presentation === "inline") {
      void staffBooking.seriesFromAppointment(Number(appointmentId))
        .then((next) => {
          if (current) {
            setSeries(next && Array.isArray(next.following) ? next : null);
          }
        })
        .catch(() => {
          // A missing or unavailable recurrence lookup leaves single-lesson
          // cancellation exactly as it was.
          if (current) setSeries(null);
        });
    }

    return () => {
      current = false;
      onBusyChange?.(false);
    };
  }, [appointmentId, onBusyChange, presentation]);

  async function confirm(choice?: CancellationCreditResolution | PaidCancellationAction) {
    setUncertain(false);
    if (preview === null) return;

    const paidLesson = tutorCancellationIsPaidLesson(preview);
    const selectedResolution = choice === "restore" || choice === "consume" ? choice : resolution;
    const selectedPaidAction = choice === "grant_rebooking_credit" || choice === "retain_payment"
      ? choice
      : paidAction;
    if (tutorCancellationConfirmDisabled(false, preview, selectedResolution, selectedPaidAction)) {
      setError(
        paidLesson
          ? "Choose whether to issue a rebooking credit or keep the payment before confirming cancellation."
          : "Choose how the lesson credit should be handled before confirming cancellation.",
      );
      return;
    }
    setCancellationBusy(true);
    setError(null);
    try {
      // A paid lesson never used a credit, so it sends Issue/Keep payment and
      // no Return/Keep choice. FastAPI re-checks everything when it applies.
      const result = await measureAction("lesson.cancel", () =>
        paidLesson
          ? staffBooking.cancelAppointment(
              appointmentId,
              null,
              key,
              notifyParticipants,
              selectedPaidAction,
            )
          : staffBooking.cancelAppointment(
              appointmentId,
              selectedResolution ?? "restore",
              key,
              notifyParticipants,
            ),
      );
      const cancelledAt = Date.now();
      const appointmentExternalId = Number(appointmentId);
      const restoreIsSafe = bookingId !== undefined && bookingId.length > 0 &&
        Number.isSafeInteger(appointmentExternalId) && appointmentExternalId > 0 &&
        result.credits_restored === 0 &&
        (result.credits_granted ?? 0) === 0 &&
        result.credit_resolution_status === "applied" &&
        !result.credit_review_required;
      report({ variant: result.credit_review_required || result.credit_resolution_status === "requires_attention" || result.entitlement_kind === "requires_review" ? "attention" : "success",
        title: "Lesson cancelled", description: `${studentName} · ${formatDate(startsAt)} · ${formatTime(startsAt)}`,
        details: [{ value: tutorCancellationSuccess(result, notifyParticipants) }],
        action: restoreIsSafe && bookingId ? (
          <RestoreLessonAction
            appointmentId={appointmentId}
            bookingId={bookingId}
            startsAt={startsAt}
            cancelledAt={cancelledAt}
            notifyParticipants={notifyParticipants}
            onRestored={onRestored}
            onLocalOutcome={setDone}
          />
        ) : undefined,
      });
      if (Number.isSafeInteger(appointmentExternalId) && appointmentExternalId > 0) {
        onCancelled?.(appointmentExternalId);
      }
    } catch (cause) {
      if (bookingFailureKind(cause) === "uncertain") publish({ variant: "uncertain", title: "Cancellation outcome needs checking", description: `${studentName} · ${formatDate(startsAt)} · ${formatTime(startsAt)}`, details: [{ value: "Check lessons and credits before retrying; a retry reuses this cancellation attempt." }] });
      setKey(tutorCancellationRetryKey(key, cause, idempotencyKey));
      setUncertain(bookingFailureKind(cause) === "uncertain");
      setError(bookingFailureKind(cause) === "uncertain" ? "The cancellation outcome could not be confirmed. Check lessons and credits before retrying; a retry reuses this attempt." : tutorCancellationFailure(cause));
      focusedReadyAction.current = false;
      setPreview(null);
      setResolution(null);
      setPaidAction(null);
    } finally {
      setCancellationBusy(false);
    }
  }

  if (!dialogOpen) return null;

  const inlineChoicesSubmitImmediately = presentation === "inline" && preview !== null && (
    tutorCancellationIsPaidLesson(preview)
      ? preview.entitlement?.decision_required === true
      : preview.credit_resolution_required
  );
  const emailToggle = (
    <>
      <label className="row small" style={{ marginTop: 12 }}>
        <input
          type="checkbox"
          checked={notifyParticipants}
          onChange={(event) => setNotifyParticipants(event.currentTarget.checked)}
        />
        Email the customer about this cancellation
      </label>
      <div className="small muted" style={{ marginTop: 4 }}>
        {notifyParticipants ? "Participants notified" : "Notifications suppressed"}
      </div>
    </>
  );

  const content = (
    <>
      <p className="small muted">
        Tutor pay for late paid or credit lessons follows your choice here.
        Cancellations made directly in Amelia have no saved choice and are excluded from tutor invoices.
      </p>

      {error && (uncertain && !hasHost
        ? <ActionConfirmation variant="uncertain" title="Cancellation outcome needs checking" description={error} details={[{ value: "Check lessons and credits before retrying. The same cancellation attempt will be reused." }]} />
        : <div className="error small" role="alert">{error}</div>)}

      {done ? (
        !hasHost && <ActionConfirmation {...done} reveal />
      ) : seriesScope && series ? (
        <SeriesCancellation
          series={series}
          onBack={() => setSeriesScope(false)}
          onBusyChange={setCancellationBusy}
          onUncertainChange={setSeriesUncertain}
          onFinished={(outcome, changedAppointmentIds) => {
            report(outcome);
            for (const changedAppointmentId of changedAppointmentIds) {
              onCancelled?.(changedAppointmentId);
            }
          }}
        />
      ) : !preview ? (
        busy ? (
          <div className="notice small" role="status">Checking cancellation policy…</div>
        ) : (
          <button ref={retryPreviewRef} type="button" onClick={() => void look()}>
            Retry cancellation check
          </button>
        )
      ) : (
        <>
          {presentation === "inline" && series && series.following.length > 1 && (
            <div className="stack" role="group" aria-label="Cancel scope">
              <p className="small muted">Cancel scope</p>
              <label className="row small">
                <input
                  type="radio"
                  name={`cancel-scope-${appointmentId}`}
                  checked={!seriesScope}
                  disabled={busy || seriesUncertain}
                  onChange={() => setSeriesScope(false)}
                />
                This lesson
              </label>
              <label className="row small">
                <input
                  type="radio"
                  name={`cancel-scope-${appointmentId}`}
                  checked={seriesScope}
                  disabled={busy || seriesUncertain}
                  onChange={() => setSeriesScope(true)}
                />
                This and following ({series.following.length})
              </label>
            </div>
          )}

          {tutorCancellationIsLate(preview) ? (
            <div className="notice cal-late-cancel-warning" role="alert">
              <strong>Late cancellation</strong>
              <div className="small">
                {preview.notice_period_hours <= 0
                  ? tutorCancellationRequiresChoice(preview)
                    ? `This lesson has already started. The ${preview.notice_required_hours}-hour cancellation choice still applies.`
                    : "This lesson has already started."
                  : `Starts in ${tutorCancellationNotice(preview.notice_period_hours)}. This is inside the ${preview.notice_required_hours}-hour cancellation window.`}
              </div>
            </div>
          ) : (
            <p className="small">Starts in {tutorCancellationNotice(preview.notice_period_hours)}.</p>
          )}

          {tutorCancellationIsPaidLesson(preview) ? (
            <p className="small">
              This lesson has already been paid for. It did not use a lesson credit, and its
              payment stays recorded.
            </p>
          ) : preview.credits_to_restore > 0 && (
            <p className="small">
              This lesson uses {preview.credits_to_restore} lesson credit
              {preview.credits_to_restore === 1 ? "" : "s"}.
            </p>
          )}

          {inlineChoicesSubmitImmediately && emailToggle}

          {tutorCancellationIsPaidLesson(preview) && preview.entitlement?.decision_required ? (
            presentation === "inline" ? (
              <div className="stack" role="group" aria-label="What should happen to the payment?">
                <p className="small muted">What should happen to the payment?</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPaidAction("grant_rebooking_credit");
                    void confirm("grant_rebooking_credit");
                  }}
                >
                  {`Cancel · issue rebooking credit${notifyParticipants ? "" : " without email"}`}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPaidAction("retain_payment");
                    void confirm("retain_payment");
                  }}
                >
                  {`Cancel · keep payment as charge${notifyParticipants ? "" : " without email"}`}
                </button>
              </div>
            ) : (
              <fieldset className="stack" style={{ border: 0, padding: 0 }}>
                <legend className="small muted">What should happen to the payment?</legend>
                <label className="row small">
                  <input
                    ref={firstDecisionRef}
                    type="radio"
                    name={`paid-resolution-${appointmentId}`}
                    checked={paidAction === "grant_rebooking_credit"}
                    onChange={() => setPaidAction("grant_rebooking_credit")}
                  />
                  <span>
                    <strong>Issue rebooking credit</strong>
                    <br />
                    The lesson has already been paid for. Add one lesson credit to the
                    student&apos;s account so they can rebook. No refund will be made.
                  </span>
                </label>
                <label className="row small">
                  <input
                    type="radio"
                    name={`paid-resolution-${appointmentId}`}
                    checked={paidAction === "retain_payment"}
                    onChange={() => setPaidAction("retain_payment")}
                  />
                  <span>
                    <strong>Keep payment as cancellation charge</strong>
                    <br />
                    Cancel the lesson without adding a rebooking credit. The existing payment
                    remains recorded.
                  </span>
                </label>
              </fieldset>
            )
          ) : tutorCancellationIsPaidLesson(preview) ? (
            <p className="small">
              At least {preview.notice_required_hours} hours&apos; notice: one rebooking credit
              will be issued automatically. No refund will be made.
            </p>
          ) : preview.entitlement?.kind === "requires_review" ? (
            <p className="small">
              This lesson&apos;s payment can&apos;t be matched to one whole paid lesson. Cancelling
              sends it to the Action Centre to check what the family is owed.
            </p>
          ) : preview.credit_resolution_required ? (
            presentation === "inline" ? (
              <div className="stack" role="group" aria-label="How should this credit be handled?">
                <p className="small muted">How should this credit be handled?</p>
                {preview.available_credit_resolutions.map((availableResolution) => (
                  <button
                    key={availableResolution}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setResolution(availableResolution);
                      void confirm(availableResolution);
                    }}
                  >
                    {`Cancel · ${availableResolution === "restore" ? "return credit" : "keep credit used"}${notifyParticipants ? "" : " without email"}`}
                  </button>
                ))}
              </div>
            ) : (
              <fieldset className="stack" style={{ border: 0, padding: 0 }}>
                <legend className="small muted">How should this credit be handled?</legend>
                {preview.available_credit_resolutions.map((availableResolution, index) => (
                  <label className="row small" key={availableResolution}>
                    <input
                      ref={index === 0 ? firstDecisionRef : undefined}
                      type="radio"
                      name={`credit-resolution-${appointmentId}`}
                      checked={resolution === availableResolution}
                      onChange={() => setResolution(availableResolution)}
                    />
                    {availableResolution === "restore" ? "Return credit" : "Keep credit used"}
                  </label>
                ))}
              </fieldset>
            )
          ) : preview.automatic_credit_resolution === "restore" && preview.credits_to_restore > 0 ? (
            <p className="small">
              At least {preview.notice_required_hours} hours&apos; notice: the credit will be
              returned automatically.
            </p>
          ) : preview.automatic_credit_resolution === "consume" ? (
            <p className="small">The lesson credit will remain used.</p>
          ) : (
            <p className="small">No lesson credit is attached to this lesson.</p>
          )}

          {!inlineChoicesSubmitImmediately && emailToggle}

          <div className="row" style={{ marginTop: 12 }}>
            {!inlineChoicesSubmitImmediately && (
              <button
                type="button"
                disabled={tutorCancellationConfirmDisabled(busy, preview, resolution, paidAction)}
                onClick={() => void confirm()}
              >
                {busy
                  ? "Cancelling…"
                  : notifyParticipants
                    ? "Cancel and email customer"
                    : "Cancel without email"}
              </button>
            )}
            <button ref={notNowRef} type="button" className="quiet" disabled={busy} onClick={closeDialog}>
              {presentation === "inline" ? "Keep lesson" : "Not now"}
            </button>
          </div>
        </>
      )}
    </>
  );

  if (presentation === "inline") {
    return (
      <section className="change-lesson tutor-cancel-dialog" aria-labelledby={inlineHeadingId}>
        <h2 id={inlineHeadingId} ref={inlineHeadingRef} tabIndex={-1}>Cancel this lesson?</h2>
        <p className="small muted">
          Student: {studentName}
          <br />
          Lesson: {serviceName ?? "Lesson"} · {formatDate(startsAt)} at {formatTime(startsAt)}
        </p>
        {content}
      </section>
    );
  }

  return (
    <Dialog
      title="Cancel lesson?"
      description={(
        <>
          Student: {studentName}
          <br />
          Lesson: {serviceName ?? "Lesson"} · {formatDate(startsAt)} at {formatTime(startsAt)}
        </>
      )}
      onClose={closeDialog}
      restoreFocusRef={returnFocusRef}
      busy={busy}
      closeLabel="Close cancellation"
      size="medium"
      className="change-lesson tutor-cancel-dialog"
    >
      {content}
    </Dialog>
  );
}
